import { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaInfoCircle, FaShieldAlt, FaClipboardList, FaCogs, FaCheckSquare } from 'react-icons/fa';
import laporanService from '../services/laporanService';

// Registrasi elemen untuk grafik donat/pie
ChartJS.register(ArcElement, Tooltip, Legend);

const GrafikLaporan = () => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, baru: 0, diproses: 0, selesai: 0 });

    useEffect(() => {
        const sessionId = localStorage.getItem("sipuma_session_id");
        
        if (sessionId) {
            laporanService.getLaporanBySessionId(sessionId)
                .then(response => {
                    const laporan = response.data;
                    const prosesCount = laporan.filter(l => l.status === 'DIPROSES').length;
                    const selesaiCount = laporan.filter(l => l.status === 'SELESAI').length;
                    const baruCount = laporan.filter(l => l.status === 'BARU').length;
                    const totalCount = laporan.length;

                    // Simpan data angka untuk kartu ringkasan
                    setStats({
                        total: totalCount,
                        baru: baruCount,
                        diproses: prosesCount,
                        selesai: selesaiCount
                    });

                    // Konfigurasi data untuk Doughnut Chart
                    setChartData({
                        labels: ['Baru (Belum Dicek)', 'Sedang Diproses', 'Selesai'],
                        datasets: [
                            {
                                label: 'Jumlah Tiket',
                                data: [baruCount, prosesCount, selesaiCount],
                                backgroundColor: ['#ffc107', '#0d6efd', '#198754'],
                                borderColor: ['#ffffff', '#ffffff', '#ffffff'],
                                borderWidth: 3,
                                hoverOffset: 5,
                            }
                        ]
                    });
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Gagal memuat grafik", err);
                    setLoading(false);
                });
        } else {
            // State awal jika belum ada laporan sama sekali
            setStats({ total: 0, baru: 0, diproses: 0, selesai: 0 });
            setChartData({
                labels: ['Belum Ada Laporan'],
                datasets: [
                    {
                        data: [1],
                        backgroundColor: ['#e9ecef'], // Warna abu-abu kosong
                        borderWidth: 0,
                    }
                ]
            });
            setLoading(false);
        }
    }, []);

    return (
        <div className="grafik-laporan-container">
            <div className="d-flex align-items-center mb-4">
                <FaShieldAlt className="text-primary me-2 fs-3" />
                <h4 className="mb-0 fw-bold text-dark">Ringkasan Status Pengaduan Anda</h4>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Menganalisis data laporan Anda...</p>
                </div>
            ) : (
                <>
                    {/* BAGIAN 1: KARTU ANGKA BESAR (Lebih user-friendly) */}
                    <Row className="mb-4">
                        <Col md={4} className="mb-3 mb-md-0">
                            <Card className="shadow-sm border-0 bg-warning text-dark h-100 rounded-3">
                                <Card.Body className="d-flex align-items-center">
                                    <div className="bg-white p-3 rounded-circle me-3 shadow-sm">
                                        <FaClipboardList className="fs-3 text-warning" />
                                    </div>
                                    <div>
                                        <h6 className="mb-1 fw-bold">Menunggu (Baru)</h6>
                                        <h2 className="mb-0 fw-bold">{stats.baru}</h2>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mb-3 mb-md-0">
                            <Card className="shadow-sm border-0 bg-primary text-white h-100 rounded-3">
                                <Card.Body className="d-flex align-items-center">
                                    <div className="bg-white p-3 rounded-circle me-3 shadow-sm">
                                        <FaCogs className="fs-3 text-primary" />
                                    </div>
                                    <div>
                                        <h6 className="mb-1 fw-bold">Sedang Diproses</h6>
                                        <h2 className="mb-0 fw-bold">{stats.diproses}</h2>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0 bg-success text-white h-100 rounded-3">
                                <Card.Body className="d-flex align-items-center">
                                    <div className="bg-white p-3 rounded-circle me-3 shadow-sm">
                                        <FaCheckSquare className="fs-3 text-success" />
                                    </div>
                                    <div>
                                        <h6 className="mb-1 fw-bold">Selesai / Ditutup</h6>
                                        <h2 className="mb-0 fw-bold">{stats.selesai}</h2>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* BAGIAN 2: GRAFIK VISUAL DAN PENJELASAN */}
                    <Card className="shadow-sm border-0 rounded-3">
                        <Card.Body className="p-4">
                            <Row>
                                <Col md={5} className="d-flex justify-content-center align-items-center mb-4 mb-md-0">
                                    <div style={{ width: '250px', height: '250px' }}>
                                        {chartData && (
                                            <Doughnut
                                                data={chartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    cutout: '65%', // Membuat lubang donat lebih besar agar elegan
                                                    plugins: {
                                                        legend: { 
                                                            position: 'bottom',
                                                            labels: { padding: 20, font: { family: 'inherit' } }
                                                        },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function(context) {
                                                                    if(context.label === 'Belum Ada Laporan') return ' 0 Tiket';
                                                                    return ` ${context.raw} Tiket Pengaduan`;
                                                                }
                                                            }
                                                        }
                                                    },
                                                }}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col md={7} className="d-flex flex-column justify-content-center border-start ps-md-5">
                                    <h5 className="fw-bold text-dark mb-3">
                                        <FaInfoCircle className="text-info me-2" /> 
                                        Cara Membaca Status
                                    </h5>
                                    <p className="text-muted mb-4" style={{ lineHeight: '1.6' }}>
                                        Sistem SIPUMA mengelompokkan laporan Anda ke dalam 3 tahapan. Total ada <strong>{stats.total} laporan</strong> yang terdeteksi berasal dari perangkat Anda saat ini.
                                    </p>
                                    
                                    <div className="d-flex align-items-start mb-3">
                                        <div className="bg-warning text-dark fw-bold rounded px-2 py-1 me-3" style={{ width: '80px', textAlign: 'center', fontSize: '0.85rem' }}>BARU</div>
                                        <p className="text-muted small mb-0">Laporan Anda sudah masuk ke sistem kami dan sedang mengantre untuk diverifikasi oleh petugas jaga.</p>
                                    </div>
                                    <div className="d-flex align-items-start mb-3">
                                        <div className="bg-primary text-white fw-bold rounded px-2 py-1 me-3" style={{ width: '80px', textAlign: 'center', fontSize: '0.85rem' }}>DIPROSES</div>
                                        <p className="text-muted small mb-0">Petugas Kepatuhan Internal sedang melakukan investigasi atau penindakan terkait laporan yang Anda kirimkan.</p>
                                    </div>
                                    <div className="d-flex align-items-start">
                                        <div className="bg-success text-white fw-bold rounded px-2 py-1 me-3" style={{ width: '80px', textAlign: 'center', fontSize: '0.85rem' }}>SELESAI</div>
                                        <p className="text-muted small mb-0">Proses investigasi telah rampung. Silakan cek menu "Riwayat Laporan" untuk melihat balasan resmi dari instansi.</p>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </>
            )}
        </div>
    );
};

export default GrafikLaporan;